// ============================================================================
// KLINISOL CRO - Certificate Verification Page
// Public page for verifying training certificates via QR code
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
    CheckCircle, 
    XCircle, 
    AlertTriangle, 
    Shield, 
    Calendar, 
    User, 
    FileText, 
    Award,
    Loader2,
    ArrowLeft,
    QrCode
} from 'lucide-react';

const CertificateVerification = () => {
    const { certificateNumber } = useParams();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [verificationResult, setVerificationResult] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        if (certificateNumber) {
            verifyCertificate();
        }
    }, [certificateNumber]);

    const verifyCertificate = async () => {
        try {
            setLoading(true);
            setError('');
            
            const response = await axios.get(
                `https://klinisol.com/api/verify/certificate/${certificateNumber}`
            );
            
            setVerificationResult(response.data);
            
        } catch (err) {
            console.error('Verification error:', err);
            if (err.response?.data) {
                setVerificationResult(err.response.data);
            } else {
                setError('Failed to verify certificate. Please check your connection and try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch {
            return 'Invalid Date';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'VALID':
                return 'from-[#475569] to-[#E91E63]';
            case 'EXPIRED':
                return 'from-yellow-500 to-yellow-600';
            default:
                return 'from-red-500 to-red-600';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'VALID':
                return <CheckCircle className="w-16 h-16 text-white" />;
            case 'EXPIRED':
                return <AlertTriangle className="w-16 h-16 text-yellow-600" />;
            default:
                return <XCircle className="w-16 h-16 text-red-600" />;
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#f8fafd] to-[#e0e7ff] flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-16 h-16 text-[#475569] animate-spin mx-auto mb-4" />
                    <p className="text-xl text-gray-700 font-semibold">Verifying Certificate...</p>
                    <p className="text-sm text-gray-500 mt-2">Please wait while we check the authenticity</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#f8fafd] to-[#e0e7ff] flex items-center justify-center p-4">
                <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-8">
                    <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
                    <h1 className="text-3xl font-bold text-gray-900 text-center mb-4">Verification Error</h1>
                    <p className="text-gray-600 text-center mb-6">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="w-full px-6 py-3 bg-gradient-to-r from-[#475569] to-[#334155] text-white font-semibold rounded-lg hover:from-[#334155] hover:to-[#1e293b] transition-all"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#f8fafd] to-[#e0e7ff] py-12 px-4">
            {/* Header */}
            <div className="max-w-4xl mx-auto mb-8">
                <div className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white rounded-2xl p-6 shadow-lg">
                    <div className="flex items-center gap-3 mb-2">
                        <Shield className="w-8 h-8" />
                        <h1 className="text-3xl font-bold">Certificate Verification</h1>
                    </div>
                    <p className="text-white/90">KLINISOL Clinical Research Organization</p>
                </div>
            </div>

            {/* Verification Result */}
            <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                    {/* Status Banner */}
                    <div className={`bg-gradient-to-r ${getStatusColor(verificationResult?.status)} text-white p-8 text-center`}>
                        <div className="flex justify-center mb-4">
                            {getStatusIcon(verificationResult?.status)}
                        </div>
                        <h2 className="text-3xl font-bold mb-2">
                            {verificationResult?.status === 'VALID' && 'Certificate Verified'}
                            {verificationResult?.status === 'EXPIRED' && 'Certificate Expired'}
                            {verificationResult?.status === 'NOT_FOUND' && 'Certificate Not Found'}
                        </h2>
                        <p className="text-lg opacity-90">{verificationResult?.message}</p>
                    </div>

                    {/* Certificate Details */}
                    {verificationResult?.certificate && (
                        <div className="p-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Certificate Number */}
                                <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                                    <div className="flex items-center gap-2 mb-2">
                                        <FileText className="w-5 h-5 text-[#475569]" />
                                        <span className="font-semibold text-gray-700">Certificate Number</span>
                                    </div>
                                    <p className="text-lg font-mono text-gray-900 break-all">
                                        {verificationResult.certificate.certificate_id || verificationResult.certificate.certificate_number || 'N/A'}
                                    </p>
                                </div>

                                {/* Trainee Name */}
                                <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                                    <div className="flex items-center gap-2 mb-2">
                                        <User className="w-5 h-5 text-[#475569]" />
                                        <span className="font-semibold text-gray-700">Trainee Name</span>
                                    </div>
                                    <p className="text-lg text-gray-900">
                                        {verificationResult.certificate.trainee_name}
                                    </p>
                                </div>

                                {/* Module Name */}
                                <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200 md:col-span-2">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Award className="w-5 h-5 text-[#475569]" />
                                        <span className="font-semibold text-gray-700">Training Module</span>
                                    </div>
                                    <p className="text-lg text-gray-900 mb-1">
                                        {verificationResult.certificate.module_name}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        Code: {verificationResult.certificate.module_code} | 
                                        Category: {verificationResult.certificate.category} | 
                                        Duration: {verificationResult.certificate.duration_minutes} minutes
                                    </p>
                                </div>

                                {/* Certification Date */}
                                <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Calendar className="w-5 h-5 text-[#475569]" />
                                        <span className="font-semibold text-gray-700">Certified Date</span>
                                    </div>
                                    <p className="text-lg text-gray-900">
                                        {formatDate(verificationResult.certificate.certified_date)}
                                    </p>
                                </div>

                                {/* Expiry Date */}
                                <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Calendar className="w-5 h-5 text-[#475569]" />
                                        <span className="font-semibold text-gray-700">Expiry Date</span>
                                    </div>
                                    <p className="text-lg text-gray-900">
                                        {formatDate(verificationResult.certificate.expiry_date)}
                                    </p>
                                    {verificationResult.certificate.days_until_expiry > 0 && (
                                        <p className="text-sm text-gray-600 mt-1">
                                            Expires in {verificationResult.certificate.days_until_expiry} days
                                        </p>
                                    )}
                                </div>

                                {/* Quiz Score */}
                                <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Award className="w-5 h-5 text-[#475569]" />
                                        <span className="font-semibold text-gray-700">Quiz Score</span>
                                    </div>
                                    <p className="text-3xl font-bold text-[#E91E63]">
                                        {verificationResult.certificate.quiz_score || 'N/A'}
                                    </p>
                                </div>

                                {/* QR Code */}
                                <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200 flex flex-col items-center">
                                    <div className="flex items-center gap-2 mb-2">
                                        <QrCode className="w-5 h-5 text-[#475569]" />
                                        <span className="font-semibold text-gray-700">Scan to Verify</span>
                                    </div>
                                    <img 
                                        src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(window.location.href)}`}
                                        alt="Verification QR Code"
                                        className="w-28 h-28"
                                    />
                                </div>
                            </div>

                            {/* Warning for Expired */}
                            {verificationResult.status === 'EXPIRED' && verificationResult.warning && (
                                <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                                        <p className="text-yellow-800">{verificationResult.warning}</p>
                                    </div>
                                </div>
                            )}

                            {/* Verification Timestamp */}
                            <div className="mt-8 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
                                Verified on {new Date().toLocaleString()}
                            </div>
                        </div>
                    )}

                    {/* Not Found Message */}
                    {!verificationResult?.certificate && verificationResult?.details && (
                        <div className="p-8 text-center">
                            <p className="text-gray-600 mb-6">{verificationResult.details}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="max-w-4xl mx-auto mt-8 text-center text-sm text-gray-600">
                <p>© 2025 KLINiSOL Clinical Research Organization. All rights reserved.</p>
                <p className="mt-2">FDA 21 CFR Part 11 Compliant Training Platform</p>
            </div>
        </div>
    );
};

export default CertificateVerification;
